import { useCallback } from 'react';

import { ConfirmationDialog } from '@/lib/base/overlays/confirmation-dialog';

export interface DeleteCRMObjectModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal open state changes */
  onOpenChange: (isOpen: boolean) => void;
  /** Entity type label (e.g., "Contact", "Company", "Deal") */
  entityTypeLabel: string;
  /** Name of the object being deleted (e.g., "John Doe", "Acme Corp") */
  objectName: string;
  /** Callback when delete is confirmed */
  onConfirm: () => void | Promise<void>;
  /** Whether the delete operation is in progress */
  isDeleting?: boolean;
  /** Override the default description text. When omitted, uses the standard "Are you sure..." copy. */
  description?: string;
  /** Override the default title. When omitted, uses `Delete ${entityTypeLabel.toLowerCase()}`. Useful for bulk operations that need pluralization or a count in the title. */
  title?: string;
}

/**
 * Delete confirmation modal for CRM objects.
 * Uses the canonical `ConfirmationDialog` component with `variant='destructive'`.
 *
 * @example
 * ```tsx
 * <DeleteCRMObjectModal
 *   isOpen={isOpen}
 *   onOpenChange={setIsOpen}
 *   entityTypeLabel="Contact"
 *   objectName="John Doe"
 *   onConfirm={handleDelete}
 *   isDeleting={isDeleting}
 * />
 * ```
 */
export function DeleteCRMObjectModal({
  isOpen,
  onOpenChange,
  entityTypeLabel,
  objectName,
  onConfirm,
  isDeleting = false,
  description,
  title,
}: DeleteCRMObjectModalProps) {
  const handleCancel = useCallback(() => onOpenChange(false), [onOpenChange]);

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      title={title ?? `Delete ${entityTypeLabel.toLowerCase()}`}
      description={
        description ??
        `Are you sure you want to delete ${objectName}? This action cannot be undone.`
      }
      confirmLabel={isDeleting ? 'Deleting…' : 'Delete'}
      cancelLabel='Cancel'
      variant='destructive'
      isLoading={isDeleting}
      onConfirm={onConfirm}
      onCancel={handleCancel}
    />
  );
}
